import { ProjectFile } from './index';

export type VerifierRunMode = 'python-file' | 'lean-proof';

export interface VerifierRequestFile {
  name: string;
  content: string;
  type: ProjectFile['type'];
}

export interface VerifierRunPayload {
  projectId: string;
  entrypoint: string;
  runMode: VerifierRunMode;
  stdinText: string;
  args: string[];
  files: VerifierRequestFile[];
}

export const VERIFIER_PROMPT_MODELS = [
  'Qwen/Qwen3-Coder-480B-A35B-Instruct-FP8',
  'zai-org/GLM-5',
  'Qwen/Qwen3.5-397B-A17B',
  'moonshotai/Kimi-K2.5',
  'deepseek-ai/DeepSeek-V3.1',
] as const;

export type VerifierPromptModel = typeof VERIFIER_PROMPT_MODELS[number];

export const DEFAULT_VERIFIER_PROMPT_MODEL: VerifierPromptModel = 'Qwen/Qwen3-Coder-480B-A35B-Instruct-FP8';

export interface VerifierPromptOutputRequest {
  projectId: string;
  promptText: string;
  model?: string;
}

export interface VerifierPromptOutputResponse {
  output: string;
  model: string | null;
}

export type VerifierRunPhase =
  | 'idle'
  | 'prompt'
  | 'verifier';

export type VerifierUserFacingErrorCode =
  | 'ai-timeout'
  | 'ai-empty-response'
  | 'ai-rate-limited'
  | 'ai-unavailable'
  | 'ai-request-too-large'
  | 'ai-error'
  | 'configuration'
  | 'invalid-prompt'
  | 'required-file-missing'
  | 'payload-too-large'
  | 'storage-fetch-failed'
  | 'unknown';

export interface VerifierUserFacingError {
  code: VerifierUserFacingErrorCode;
  phase: Exclude<VerifierRunPhase, 'idle'>;
  title: string;
  message: string;
  guidance?: string;
  retryable: boolean;
}

export interface VerifierRunTicketRequest {
  projectId: string;
  entrypoint: string;
  runMode: VerifierRunMode;
  snapshotHash: string;
  fileCount: number;
  totalBytes: number;
}

export interface VerifierRunTicketResponse {
  ticket: string;
  expiresAt: number;
  runMode: VerifierRunMode;
}

export interface VerifierStartedEvent {
  type: 'started';
  runId: string;
  entrypoint: string;
}

export interface VerifierTextEvent {
  type: 'stdout' | 'stderr' | 'system';
  text: string;
}

export interface VerifierExitEvent {
  type: 'exit';
  exitCode: number | null;
  signal: number | null;
  timedOut: boolean;
  aborted?: boolean;
  outputLimitExceeded?: boolean;
  durationMs: number;
}

export type VerifierStreamEvent =
  | VerifierStartedEvent
  | VerifierTextEvent
  | VerifierExitEvent;

export type VerifierRunStatus =
  | 'idle'
  | 'starting'
  | 'running'
  | 'success'
  | 'failed'
  | 'timed_out'
  | 'aborted';
